/*
This component wraps the form input controls and generates the error message.
It also adds the necessary bootstrap classes to display error feedback.
It works on Reactive forms and template-driven forms.

See _docs/input.md for more details (need to install markdown plugin for intellij)
 */
import { Component, Input, OnInit, ContentChild, AfterContentInit, ElementRef, Renderer2, OnDestroy, DoCheck } from '@angular/core';
import { FormControlDirective, NgModel, FormControlName } from '@angular/forms';

@Component({
  selector: 'townip-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss']
})
export class InputComponent implements OnInit, AfterContentInit, OnDestroy, DoCheck {

  @Input()
  public displayErrors = true;

  @Input()
  public displayErrorMessage = true;

  @ContentChild(NgModel, /* TODO: add static flag */ {})
  public accessor: NgModel;

  @ContentChild(FormControlDirective, /* TODO: add static flag */ {})
  public reactiveAccessor: FormControlDirective;

  @ContentChild(FormControlName, /* TODO: add static flag */ {})
  public reactiveGroupedAccessor: FormControlName;

  public label: string;

  public mainAccessor: FormControlName | FormControlDirective | NgModel;

  constructor(private el: ElementRef, private renderer: Renderer2) { }

  public ngOnInit(): void {

  }

  public ngAfterContentInit(): void {
    this.getLabel();

    // Reactive forms grouped mode. (ex: when using FormGroup)
    if (this.reactiveGroupedAccessor) {
      this.initReactiveGroupMode();
      return;
    }

    // Reactive forms mode
    if (this.reactiveAccessor) {
      this.initReactiveMode();
      return;
    }

    // Template-driven forms-mode
    this.initTemplateMode();
  }

  public ngOnDestroy(): void {

  }

  public ngDoCheck(): void {
    if (!this.mainAccessor) {
      return;
    }

    // Not dirty check until we can find a way to hook an event on blur.
    if (this.mainAccessor.invalid && this.mainAccessor.touched) {
      this.addInvalidClasses();
    }
  }

  private initTemplateMode(): void {
    if (!this.accessor) {
      return;
    }

    this.mainAccessor = this.accessor;
    this.accessor.statusChanges.subscribe((status) => {
      if (this.accessor.invalid && this.accessor.touched) {
        this.addInvalidClasses();
      } else {
        this.removeInvalidClasses();
      }
    });
  }

  private initReactiveMode(): void {
    if (!this.reactiveAccessor) {
      return;
    }

    this.mainAccessor = this.reactiveAccessor;
    this.reactiveAccessor.form.statusChanges.subscribe((status) => {
      if (this.reactiveAccessor.invalid && this.reactiveAccessor.touched) {
        this.addInvalidClasses();
      } else {
        this.removeInvalidClasses();
      }
    });
  }

  private initReactiveGroupMode(): void {
    if (!this.reactiveGroupedAccessor) {
      return;
    }

    this.mainAccessor = this.reactiveGroupedAccessor;
    this.reactiveGroupedAccessor.statusChanges.subscribe((status) => {
      if (this.reactiveGroupedAccessor.invalid && this.reactiveGroupedAccessor.touched) {
        this.addInvalidClasses();
      } else {
        this.removeInvalidClasses();
      }
    });
  }

  private getLabel(): void {
    const el: Element = this.el.nativeElement.querySelector('label');
    if (el) {
      this.label = el.textContent;
    }
  }

  private addInvalidClasses(): void {
    if (this.displayErrors === false) {
      return;
    }

    try {
      const el = this.el.nativeElement.querySelector('.form-control');
      this.renderer.addClass(el, 'is-invalid');
    } catch (e) {
      console.warn(`Control ${this.label} is not supported.`);
      console.warn(`
        Make sure it has the \'form-control custom-control\' class.
        If it's a custom form control, make sure you have the proper styling in .custom-control depending
        on the type of custom control you are using.
      `);
    }
  }

  private removeInvalidClasses(): void {
    if (this.displayErrors === false) {
      return;
    }

    try {
      const el = this.el.nativeElement.querySelector('.form-control');
      this.renderer.removeClass(el, 'is-invalid');
    } catch (e) {
      console.warn(`Control ${this.label} is not supported.`);
      console.warn(`
        Make sure it has the \'form-control custom-control\' class.
        If it's a custom form control, make sure you have the proper styling in .custom-control depending
        on the type of custom control you are using.
      `);
    }
  }
}
